import { ConnectError } from '@bufbuild/connect';
import { yupResolver } from '@hookform/resolvers/yup';
import { equals } from 'ramda';
import { FC, useCallback, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { InviteStaffReq } from '@/shared/api/protocol-ts/api/core/svc_access_pb';
import { OrganizationStaffRoleType } from '@/shared/api/protocol-ts/model/dto_organization_staff_pb';
import { UserBadRequestError } from '@/shared/config';
import {
  useAppDispatch,
  useAppSelector,
  useIsNonDentalOrganization,
} from '@/shared/hooks';
import { normalizeEmail, safeJSONParse } from '@/shared/lib';
import { InlineFormError, Input, Modal, Select } from '@/shared/ui';

import { accessModel } from '@/entities/access';
import { ModalID, modalModel } from '@/entities/modal';
import {
  i18n,
  OrganizationMember,
  organizationModel,
  useGetOrganizationDoctorsInterfaces,
  useSpecializationsOptions,
} from '@/entities/organization';
import { organizationStaffModel } from '@/entities/organizationStaff';

import { errorToastCaller } from '@/features/errorToastCaller';

import { getValidationSchema, StaffFormPayload } from '../config/formSchema';
import { usePermissionOptions } from '../hooks/usePermissionOptions';

import styles from './AddStaffModal.module.scss';
import {
  StaffModalMultiValueRemove,
  StaffModalSelectMultiValueLabel,
  StaffModalSelectOption,
} from './AddStafModalSelect.components';

export type AddStaffModalProps = {
  className?: string;
};

export const AddStaffModal: FC<AddStaffModalProps> = () => {
  const dispatch = useAppDispatch();
  const {
    visible,
    data: { actionType, editingUserID, doctorInfo },
  } = useAppSelector((state) => state.modal.AddStaffModal);

  const companyDoctors = useGetOrganizationDoctorsInterfaces();

  const { formatMessage } = useIntl();

  const isNonDental = useIsNonDentalOrganization();
  const specializationOptions = useSpecializationsOptions({ isNonDental });
  const permissionOptions = usePermissionOptions();

  const organizationID = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );

  const handleClose = useCallback(() => {
    dispatch(modalModel.actions.closeModal(ModalID.AddStaffModal));
  }, []);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    formState: { isSubmitting, isSubmitted, errors },
  } = useForm<StaffFormPayload>({
    // @ts-expect-error WARN: Resolver badly reacts on union type
    resolver: yupResolver(getValidationSchema(actionType === 'Add')),
    mode: 'all',
  });

  const onSubmitEditStaff: SubmitHandler<StaffFormPayload> = async ({
    firstName,
    lastName,
    accessLevel,
    position,
  }: StaffFormPayload) => {
    const staffID =
      doctorInfo && 'ID' in doctorInfo ? doctorInfo.ID : editingUserID;

    const isRoleChanged = accessLevel[0] !== doctorInfo?.Role;

    try {
      const { Staff: updatedStaffFromName } = await dispatch(
        organizationModel.thunks.setOrganizationStaffName({
          StaffID: staffID,
          LastName: lastName,
          FirstName: firstName,
        }),
      ).unwrap();

      if (updatedStaffFromName) {
        dispatch(
          organizationStaffModel.actions.setNewestOne(updatedStaffFromName),
        );
      }

      if (isRoleChanged) {
        const { OrganizationStaff: updatedStaffFromRole } = await dispatch(
          accessModel.thunks.setOrganizationStaffRole({
            StaffID: staffID,
            Role: accessLevel[0],
          }),
        ).unwrap();

        if (updatedStaffFromRole) {
          dispatch(
            organizationStaffModel.actions.setNewestOne(updatedStaffFromRole),
          );
        }
      }

      const { Staff: updatedStaffFromPositions } = await dispatch(
        organizationModel.thunks.setOrganizationStaffJobPositions({
          StaffID: staffID,
          JobPositions: position,
        }),
      ).unwrap();

      if (updatedStaffFromPositions) {
        dispatch(
          organizationStaffModel.actions.setNewestOne(
            updatedStaffFromPositions,
          ),
        );
      }

      handleClose();
    } catch (error) {
      errorToastCaller(error as ConnectError);
    }
  };

  const getDefaultValues = (doctor: OrganizationMember) => ({
    firstName: doctor.PersonalData?.FirstName,
    lastName: doctor.PersonalData?.LastName,
    accessLevel: [doctor.Role],
    position: doctor.JobPositions,
    email: doctor.PersonalData?.Email,
  });

  const handleExistingEmail = (value: string) => {
    const alreadyExistingDoctor = companyDoctors.find(
      (doctor) =>
        (doctor.doctorInfo as OrganizationMember).PersonalData?.Email === value,
    );

    if (alreadyExistingDoctor) {
      setError('email', {
        type: 'custom',
        message: formatMessage({
          id: 'addStaffModal.emailError',
          defaultMessage: 'Invitation to this organization role already exist.',
        }),
      });
    }
  };

  const onSubmitAddStaff: SubmitHandler<StaffFormPayload> = async (data) => {
    const { lastName, firstName, position, accessLevel } = data;
    const email = normalizeEmail(data.email);

    try {
      await dispatch(
        accessModel.thunks.inviteStaff(
          new InviteStaffReq({
            RecipientEmail: email,
            OrganizationID: organizationID,
            FirstName: firstName,
            LastName: lastName,
            JobPositions: position,
            Role: accessLevel[0] as number as OrganizationStaffRoleType,
          }),
        ),
      ).unwrap();

      handleClose();
    } catch (error) {
      const connectError = ConnectError.from(error);
      const parsedMessage = safeJSONParse(connectError.rawMessage);

      if (
        parsedMessage?.code ===
        UserBadRequestError.INVITATION_RECIPIENT_ALREADY_HAS_ORGANIZATION_ROLE
      ) {
        setError('email', {
          type: UserBadRequestError.INVITATION_RECIPIENT_ALREADY_HAS_ORGANIZATION_ROLE,
          message: formatMessage({
            id: 'addStaffModal.alreadyInviteError',
            defaultMessage:
              'Team member with provided email has already been invited.',
          }),
        });
      }
    }
  };

  const submitFunction = (data: StaffFormPayload) => {
    if (actionType === 'Add') {
      return onSubmitAddStaff(data);
    }
    return onSubmitEditStaff(data);
  };

  useEffect(() => {
    if (actionType === 'Edit' && doctorInfo) {
      reset(getDefaultValues(doctorInfo as OrganizationMember));
    }

    return () => {
      reset({
        firstName: '',
        lastName: '',
        email: '',
        accessLevel: [],
        position: [],
      });
    };
  }, [reset, doctorInfo, visible]);

  const isAlreadyInvitedEmailError =
    errors?.email &&
    equals(
      errors.email.type,
      UserBadRequestError.INVITATION_RECIPIENT_ALREADY_HAS_ORGANIZATION_ROLE,
    );

  return (
    <Modal
      data-component="add-staff-modal"
      title={
        actionType === 'Add'
          ? formatMessage({
              id: 'addStaffModal.title',
              defaultMessage: 'Add Staff',
            })
          : formatMessage({
              id: 'addStaffModal.editTitle',
              defaultMessage: 'Edit Staff',
            })
      }
      isOpen={visible}
      onCancel={handleClose}
      className={styles.modal}
      containerClassName={styles.modalContainer}
      borderless
      okButtonText={formatMessage({
        id: 'global.save',
        defaultMessage: 'Save',
      })}
      okButtonProps={{
        type: 'submit',
        form: 'addStaff',
        loading: isSubmitting,
      }}
      cancelButtonText={formatMessage({
        id: 'global.cancel',
        defaultMessage: 'Cancel',
      })}
    >
      <form
        id="addStaff"
        onSubmit={handleSubmit(submitFunction)}
        className={styles.form}
      >
        <div className={styles.row}>
          <Controller
            control={control}
            name="firstName"
            render={({
              field: { ref, value, name, onBlur, onChange },
              fieldState: { error },
            }) => (
              <Input
                required
                ref={ref}
                value={value}
                name={name}
                onBlur={() => {
                  if (isSubmitted) {
                    onBlur();
                  }
                }}
                onChange={(event) => {
                  if (!isSubmitted) {
                    setValue(name, event.target.value);
                  } else {
                    onChange(event);
                  }
                }}
                type="text"
                label={formatMessage({
                  id: 'addStaff.firstName',
                  defaultMessage: 'First name',
                })}
                error={error?.message}
                autoComplete="given-name"
              />
            )}
          />

          <Controller
            control={control}
            name="lastName"
            render={({
              field: { ref, value, name, onBlur, onChange },
              fieldState: { error },
            }) => (
              <Input
                required
                ref={ref}
                value={value}
                name={name}
                onBlur={() => {
                  if (isSubmitted) {
                    onBlur();
                  }
                }}
                onChange={(event) => {
                  if (!isSubmitted) {
                    setValue(name, event.target.value);
                  } else {
                    onChange(event);
                  }
                }}
                type="text"
                label={formatMessage({
                  id: 'addStaff.lastName',
                  defaultMessage: 'Last name',
                })}
                error={error?.message}
                autoComplete="family-name"
              />
            )}
          />
        </div>

        <Controller
          control={control}
          name="email"
          render={({
            field: { ref, value, name, onBlur, onChange },
            fieldState: { error, isTouched },
          }) => (
            <Input
              required
              ref={ref}
              disabled={actionType === 'Edit'}
              value={value}
              name={name}
              onBlur={() => {
                onBlur();
                handleExistingEmail(value);
              }}
              onChange={(event) => {
                if (!isTouched) {
                  setValue(name, event.target.value);
                } else {
                  onChange(event);
                }
              }}
              label={formatMessage({
                id: 'addStaff.email',
                defaultMessage: 'Email',
              })}
              error={isAlreadyInvitedEmailError ? ' ' : error?.message}
              autoComplete="email"
            />
          )}
        />

        <Controller
          control={control}
          name="accessLevel"
          render={({
            field: { ref, value, name, onBlur },
            fieldState: { error },
          }) => {
            const isValueArray = Array.isArray(value);

            const isOwnerRole =
              isValueArray &&
              value[0] ===
                OrganizationStaffRoleType.OrganizationStaffRoleType_FullAccess;

            return (
              <Select
                label={formatMessage({
                  id: 'addStaff.accessLevel',
                  defaultMessage: 'Access level',
                })}
                options={
                  isOwnerRole
                    ? [
                        {
                          label: formatMessage(
                            i18n.permissionOwners[
                              OrganizationStaffRoleType
                                .OrganizationStaffRoleType_FullAccess
                            ],
                          ),
                          value:
                            OrganizationStaffRoleType.OrganizationStaffRoleType_FullAccess,
                          comment: formatMessage(
                            i18n.permissionOwnersComments[
                              OrganizationStaffRoleType
                                .OrganizationStaffRoleType_FullAccess
                            ],
                          ),
                        },
                      ]
                    : permissionOptions
                }
                isRequired
                ref={ref}
                value={isValueArray ? value[0] : undefined}
                components={{
                  // @ts-expect-error WARN: hand to match type with Select lib
                  Option: StaffModalSelectOption,
                }}
                name={name}
                onBlur={onBlur}
                onChange={(newValue) => {
                  // @ts-expect-error WARN: hand to match type with Select lib
                  setValue(name, [newValue]);
                }}
                error={error?.message}
                isDisabled={isOwnerRole}
              />
            );
          }}
        />

        <Controller
          control={control}
          name="position"
          render={({
            field: { ref, value, name, onBlur, onChange },
            fieldState: { error },
          }) => (
            <Select
              label={formatMessage({
                id: 'addStaff.jobPosition',
                defaultMessage: 'Job position',
              })}
              options={specializationOptions}
              isRequired
              isMulti
              components={{
                // @ts-expect-error WARN: hand to match type with Select lib
                MultiValueLabel: StaffModalSelectMultiValueLabel,
                MultiValueRemove: StaffModalMultiValueRemove,
              }}
              ref={ref}
              value={value}
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              error={error?.message}
            />
          )}
        />

        {isAlreadyInvitedEmailError && (
          <InlineFormError
            className={styles.inlineFormError}
            errorMessage={errors.email?.message}
          />
        )}
      </form>
    </Modal>
  );
};
